import React, { useState } from "react";
import axios from "axios";
import { useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { useActionRequest } from "../../../common/hooks/api";
import { selfActions } from "../../../common/reducers/selfSlice";
import { Api } from "../../../common/service/Api";
import { UserDetailsDto } from "../../../common/service/server/dto/UserDto";
import { Forms } from "../../components";
import { AuthPageWrapper } from "../../components/layout/AuthPageWrapper";
import { Error as RequestsError } from "../../copy/requests";
import { Routes } from "../../root/routes";
import { useLocalStorage } from "../../hooks/useLocalStorage";

export const LOGIN_TARGET_KEY = "POLARIS_LOGIN_TARGET";

export const Login = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [error, setError] = useState(null);

  const target = new URLSearchParams(location.search).get("target");
  const admin = new URLSearchParams(location.search).get("admin");
  const queryClient = useQueryClient();

  const [persistedTarget, setPersistedTarget] = useLocalStorage(LOGIN_TARGET_KEY, "");

  const forwardLocation = target || persistedTarget || admin;

  const { fetch: login, request: loginRequest } = useActionRequest({
    api: async (values) => Api.login.login((values as any).email, (values as any).password),
    onSuccess: (payload: UserDetailsDto | { requiresMfa: true }) => {
      if (payload?.requiresMfa) {
        window.location.href = Routes.auth.mfa;
        return;
      }
      const user = payload as UserDetailsDto;
      queryClient.invalidateQueries(["self"]);
      if (forwardLocation) {
        setPersistedTarget("");
        window.location.href = forwardLocation;
      } else {
        dispatch(selfActions.set(user as UserDetailsDto));
      }
    },
    onError: (_state, e) => {
      setPersistedTarget("");
      if (axios.isAxiosError(e) && e.response?.status == 401) {
        setError("Incorrect Email/Password");
      } else if (axios.isAxiosError(e) && e.response?.status == 429) {
        setError("Max login attempts reached, please wait 10 minutes and try again.");
      } else {
        setError("Something went wrong. Please try again later.");
      }
    },
    messages: {
      defaultError: RequestsError.Login,
    },
  });

  const onSubmit = async (...args) => {
    try {
      await login.apply(null, args);
    } catch (e) {
      console.error(e.message);
    }
  };

  return (
    <AuthPageWrapper>
      <img
        src="/assets/images/truevault-logo.png"
        alt="TrueVault Logo"
        width="262px"
        height="57px"
        style={{
          margin: "0 auto",
        }}
      />
      <Forms.Login onSubmit={onSubmit} loginRequest={loginRequest} error={error} />
    </AuthPageWrapper>
  );
};
